// ── Coach Actions Envelope ──────────────────────────────────────────────────
// Single JSON-payload protocol that replaces three brittle fenced markdown
// blocks (workout-plan / workout-delete / training-plan-update).
//
// The AI emits ONE `coach-actions` block per response, placed at the END.
// The app parses it ONCE, validates against a per-type schema, and surfaces
// the result to the user as a stack of ProposalCard components (each with
// Confirm/Discard buttons). The AI never directly mutates state.
//
// Capability profiles gate which action types are allowed:
//   - chat   : add_workouts, remove_workouts, add_phase, modify_phase,
//              remove_phase, add_milestone, remove_milestone
//   - wizard : chat set + set_plan  (set_plan is destructive — wizard only)
//
// `set_plan` is intentionally NOT in chat: replacing the whole season plan
// needs guarded UX (race date, hours, etc.) the chat surface can't safely
// gather. See plan.md for the strategic split. Multi-week plan building
// from chat is done by composing multiple add_phase + add_workouts +
// add_milestone proposals, each individually confirmable.

export const CAPABILITY_PROFILES = {
  chat: [
    'add_workouts',
    'remove_workouts',
    'add_phase',
    'modify_phase',
    'remove_phase',
    'add_milestone',
    'remove_milestone',
    'request_clarification',
  ],
  wizard: [
    'add_workouts',
    'remove_workouts',
    'add_phase',
    'modify_phase',
    'remove_phase',
    'add_milestone',
    'remove_milestone',
    'set_plan',
    'request_clarification',
  ],
};

const VALID_SPORTS = ['swim', 'bike', 'run', 'strength', 'brick', 'other'];
const VALID_PHASE_TYPES = ['base', 'build', 'peak', 'taper', 'race'];
const VALID_MILESTONE_TYPES = [
  'ftp_test',
  'race_simulation',
  'taper_start',
  'race',
  'key_workout',
  'rest_week',
];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Tolerant block regex — matches the rubber-duck recommendation to accept
// case drift, whitespace around the tag, and \r\n line endings. The model
// is instructed to emit lower-case `coach-actions` at the end of the
// response; this just guards against routine LLM format slop.
const COACH_ACTIONS_RE = /```\s*coach-actions\s*\r?\n([\s\S]*?)```/gi;

/**
 * Remove the coach-actions block(s) from a response so the prose can be
 * rendered without the raw JSON leaking into chat UI or AI history.
 */
export function stripCoachActionsBlock(text) {
  if (typeof text !== 'string' || !text) return text || '';
  return text.replace(COACH_ACTIONS_RE, '').replace(/\n{3,}/g, '\n\n').trim();
}

// ── Per-type validators ─────────────────────────────────────────────────────
// Each returns { ok: true, value } on success or { ok: false, reason } on
// reject. Validators are intentionally lenient — they normalize where they
// can (e.g. coerce duration to a number) rather than rejecting outright,
// to absorb the kinds of small drift the LLM tends to produce.

function isNonEmptyString(v) {
  return typeof v === 'string' && v.trim().length > 0;
}

function coerceISODate(v) {
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  if (ISO_DATE_RE.test(trimmed)) return trimmed;
  // Accept full ISO timestamps too (just take the date portion)
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
}

function coerceDuration(v) {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return Math.round(v);
  if (typeof v === 'string') {
    const m = v.match(/(\d+(?:\.\d+)?)\s*(min|hour|hr|h)?/i);
    if (!m) return null;
    let n = parseFloat(m[1]);
    if (m[2] && m[2].toLowerCase().startsWith('h')) n *= 60;
    return n > 0 ? Math.round(n) : null;
  }
  return null;
}

function validateWorkoutItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const date = coerceISODate(raw.date);
  if (!date) return null;
  const sport = typeof raw.sport === 'string' ? raw.sport.toLowerCase() : null;
  if (!sport || !VALID_SPORTS.includes(sport)) return null;
  if (!isNonEmptyString(raw.title)) return null;
  const duration = coerceDuration(raw.duration);
  if (!duration) return null;
  const zone =
    typeof raw.zone === 'string' && /^z[1-5]$/i.test(raw.zone.trim())
      ? raw.zone.trim().toUpperCase()
      : '';
  const description = isNonEmptyString(raw.description) ? raw.description.trim() : '';
  return { date, sport, title: raw.title.trim(), duration, zone, description };
}

function validateAddWorkouts(p) {
  const rawItems = Array.isArray(p.items)
    ? p.items
    : Array.isArray(p.workouts)
      ? p.workouts
      : [];
  if (rawItems.length === 0) {
    return { ok: false, reason: 'add_workouts requires items[]' };
  }
  const items = rawItems.map(validateWorkoutItem).filter(Boolean);
  if (items.length === 0) return { ok: false, reason: 'add_workouts: no valid items' };
  return { ok: true, value: { items } };
}

function validateRemoveWorkouts(p) {
  const rawIds = Array.isArray(p.workoutIds)
    ? p.workoutIds
    : Array.isArray(p.items)
      ? p.items
      : [];
  if (rawIds.length === 0) {
    return { ok: false, reason: 'remove_workouts requires workoutIds[]' };
  }
  const ids = rawIds
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object') {
        if (isNonEmptyString(entry.id)) return entry.id;
        if (isNonEmptyString(entry.workoutId)) return entry.workoutId;
      }
      return null;
    })
    .filter((v) => v !== null && v !== undefined) // filter out nulls from object extraction
    .filter(isNonEmptyString)
    .map((s) => s.trim());
  if (ids.length === 0) return { ok: false, reason: `remove_workouts: no valid ids (raw had ${rawIds.length} entries)` };
  return { ok: true, value: { workoutIds: ids } };
}

function validatePhaseChanges(changes) {
  if (!changes || typeof changes !== 'object') return null;
  const out = {};
  if (changes.name !== undefined) {
    if (!isNonEmptyString(changes.name)) return null;
    out.name = changes.name.trim();
  }
  if (changes.type !== undefined) {
    const t = String(changes.type).toLowerCase();
    if (!VALID_PHASE_TYPES.includes(t)) return null;
    out.type = t;
  }
  if (changes.startDate !== undefined) {
    const d = coerceISODate(changes.startDate);
    if (!d) return null;
    out.startDate = d;
  }
  if (changes.endDate !== undefined) {
    const d = coerceISODate(changes.endDate);
    if (!d) return null;
    out.endDate = d;
  }
  if (changes.notes !== undefined) {
    if (typeof changes.notes !== 'string') return null;
    out.notes = changes.notes.trim();
  }
  if (changes.weeklyTSS !== undefined) {
    const n = Number(changes.weeklyTSS);
    if (!Number.isFinite(n) || n < 0) return null;
    out.weeklyTSS = Math.round(n);
  }
  if (changes.targetRaceId !== undefined) {
    if (typeof changes.targetRaceId !== 'string') return null;
    out.targetRaceId = changes.targetRaceId.trim() || undefined;
  }
  return Object.keys(out).length > 0 ? out : null;
}

function validateModifyPhase(p) {
  if (!isNonEmptyString(p.phaseId)) {
    return { ok: false, reason: 'modify_phase requires phaseId' };
  }
  const changes = validatePhaseChanges(p.changes);
  if (!changes) return { ok: false, reason: 'modify_phase: invalid changes' };
  return { ok: true, value: { phaseId: p.phaseId.trim(), changes } };
}

function validateRemovePhase(p) {
  if (!isNonEmptyString(p.phaseId)) {
    return { ok: false, reason: 'remove_phase requires phaseId' };
  }
  return { ok: true, value: { phaseId: p.phaseId.trim() } };
}

function validateMilestoneObject(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const date = coerceISODate(raw.date);
  if (!date) return null;
  const labelOrTypeOrName =
    isNonEmptyString(raw.label) || isNonEmptyString(raw.type) || isNonEmptyString(raw.name);
  if (!labelOrTypeOrName) return null;
  const out = { date };
  if (isNonEmptyString(raw.label)) out.label = raw.label.trim();
  if (isNonEmptyString(raw.name)) out.name = raw.name.trim();
  if (isNonEmptyString(raw.type)) {
    const t = raw.type.toLowerCase().trim();
    if (!VALID_MILESTONE_TYPES.includes(t)) return null;
    out.type = t;
  }
  if (isNonEmptyString(raw.targetRaceId)) out.targetRaceId = raw.targetRaceId.trim();
  if (isNonEmptyString(raw.notes)) out.notes = raw.notes.trim();
  return out;
}

function validateAddMilestone(p) {
  const milestone = validateMilestoneObject(p.milestone);
  if (!milestone) return { ok: false, reason: 'add_milestone: invalid milestone' };
  return { ok: true, value: { milestone } };
}

function validateRemoveMilestone(p) {
  if (!isNonEmptyString(p.milestoneId)) {
    return { ok: false, reason: 'remove_milestone requires milestoneId' };
  }
  return { ok: true, value: { milestoneId: p.milestoneId.trim() } };
}

function validatePhaseObject(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (!isNonEmptyString(raw.name)) return null;
  const startDate = coerceISODate(raw.startDate);
  const endDate = coerceISODate(raw.endDate);
  if (!startDate || !endDate) return null;
  const out = { name: raw.name.trim(), startDate, endDate };
  if (isNonEmptyString(raw.type)) {
    const t = raw.type.toLowerCase().trim();
    if (!VALID_PHASE_TYPES.includes(t)) return null;
    out.type = t;
  }
  if (isNonEmptyString(raw.notes)) out.notes = raw.notes.trim();
  if (raw.weeklyTSS !== undefined) {
    const n = Number(raw.weeklyTSS);
    if (Number.isFinite(n) && n >= 0) out.weeklyTSS = Math.round(n);
  }
  if (isNonEmptyString(raw.targetRaceId)) out.targetRaceId = raw.targetRaceId.trim();
  return out;
}

function validateAddPhase(p) {
  const phase = validatePhaseObject(p.phase);
  if (!phase) return { ok: false, reason: 'add_phase: invalid phase' };
  return { ok: true, value: { phase } };
}

function validateSetPlan(p) {
  const phases = Array.isArray(p.phases) ? p.phases.map(validatePhaseObject).filter(Boolean) : [];
  const milestones = Array.isArray(p.milestones)
    ? p.milestones.map(validateMilestoneObject).filter(Boolean)
    : [];
  if (phases.length === 0 && milestones.length === 0) {
    return { ok: false, reason: 'set_plan: no valid phases or milestones' };
  }
  return { ok: true, value: { phases, milestones } };
}

const VALIDATORS = {
  add_workouts: validateAddWorkouts,
  remove_workouts: validateRemoveWorkouts,
  modify_phase: validateModifyPhase,
  remove_phase: validateRemovePhase,
  add_milestone: validateAddMilestone,
  remove_milestone: validateRemoveMilestone,
  add_phase: validateAddPhase,
  set_plan: validateSetPlan,
};

/**
 * Parse a coach-actions envelope out of an AI response.
 *
 * Returns an object:
 *   {
 *     proposals: [...],           // valid, normalized proposals
 *     rejectedCount: number,      // proposals dropped due to schema or capability
 *     rejectedReasons: [...],     // strings, for diagnostics + inline warning
 *     parseError: null | string,  // set if envelope itself is malformed
 *     hasBlock: boolean,          // true if a coach-actions fence was present
 *     contentWithoutBlock: string // input text minus the block(s), for display
 *   }
 *
 * Proposal IDs are NOT assigned here — the caller (App.js) assigns
 * `${botMsgId}_p${index}` at message-attach time. LLM-supplied IDs are
 * ignored entirely (LLM IDs are unreliable for tracking confirm/dismiss).
 *
 * Workout item IDs are also NOT assigned here — they're generated at
 * apply time, again to avoid trusting LLM IDs.
 */
export function parseCoachActions(text, { allowedActions = 'chat' } = {}) {
  const allowed = Array.isArray(allowedActions)
    ? allowedActions
    : CAPABILITY_PROFILES[allowedActions] || CAPABILITY_PROFILES.chat;

  const empty = {
    proposals: [],
    rejectedCount: 0,
    rejectedReasons: [],
    parseError: null,
    hasBlock: false,
    contentWithoutBlock: typeof text === 'string' ? text : '',
  };

  if (typeof text !== 'string' || !text) return empty;

  // Reset regex state (it's /g)
  COACH_ACTIONS_RE.lastIndex = 0;
  const matches = [...text.matchAll(COACH_ACTIONS_RE)];
  if (matches.length === 0) return empty;

  // Use the LAST block in the response — the prompt instructs the model to
  // place it at the very end. If for some reason there are multiple blocks
  // (model drift), the last one is the most likely to be "final".
  const block = matches[matches.length - 1];
  const blockBody = block[1].trim();
  const contentWithoutBlock = stripCoachActionsBlock(text);

  let envelope;
  try {
    envelope = JSON.parse(blockBody);
  } catch (err) {
    return {
      ...empty,
      hasBlock: true,
      contentWithoutBlock,
      parseError: 'JSON parse failed: ' + (err.message || 'unknown'),
    };
  }

  const rawProposals = Array.isArray(envelope?.proposals)
    ? envelope.proposals
    : Array.isArray(envelope?.actions)
      ? envelope.actions
      : null;

  if (!envelope || typeof envelope !== 'object' || !Array.isArray(rawProposals)) {
    return {
      ...empty,
      hasBlock: true,
      contentWithoutBlock,
      parseError: 'Envelope missing proposals[] array',
    };
  }

  const proposals = [];
  const rejectedReasons = [];

  rawProposals.forEach((raw, idx) => {
    if (!raw || typeof raw !== 'object' || typeof raw.type !== 'string') {
      rejectedReasons.push(`proposal[${idx}]: not an object or missing type`);
      return;
    }
    const type = raw.type.toLowerCase().trim();
    if (!allowed.includes(type)) {
      rejectedReasons.push(`proposal[${idx}] (${type}): not allowed in this capability profile`);
      return;
    }
    const validator = VALIDATORS[type];
    if (!validator) {
      rejectedReasons.push(`proposal[${idx}] (${type}): unknown type`);
      return;
    }
    const summary = isNonEmptyString(raw.summary)
      ? raw.summary.trim()
      : defaultSummary(type, raw);
    const result = validator(raw);
    if (!result.ok) {
      rejectedReasons.push(`proposal[${idx}] (${type}): ${result.reason}`);
      return;
    }
    proposals.push({
      type,
      summary,
      payload: result.value,
    });
  });

  return {
    proposals,
    rejectedCount: rejectedReasons.length,
    rejectedReasons,
    parseError: null,
    hasBlock: true,
    contentWithoutBlock,
  };
}

/**
 * Parse a STRUCTURED-OUTPUT envelope (returned by AICoach.generateStructuredResponse)
 * and convert it to the same `{ proposals, rejectedCount, ... }` shape that
 * parseCoachActions returns for the prose path. This lets App.js dispatch
 * either path uniformly.
 *
 * Input shape (per the JSON schema in src/utils/coachActionsSchema.js):
 *   { summary_message: string, proposals: Array<{ type, summary, add_workouts, remove_workouts, ... }> }
 *
 * Where each proposal has SEVEN per-type payload fields; exactly one is
 * non-null, matching `type`. The other six are null (the schema enforces
 * the shape; the model produces the discriminant).
 *
 * Output: identical shape to parseCoachActions(), so the caller can use
 * either result interchangeably.
 *
 *   {
 *     proposals: [...],           // normalized to legacy payload-wrapped shape
 *     rejectedCount: 0,           // schema prevents schema-level rejections
 *     rejectedReasons: [],        // empty
 *     parseError: null,
 *     hasBlock: true,             // we DID receive structured output
 *     contentWithoutBlock: env.summary_message,  // for the bot bubble
 *   }
 */
export function parseStructuredEnvelope(envelope, { allowedActions = 'chat' } = {}) {
  const allowed = Array.isArray(allowedActions)
    ? allowedActions
    : CAPABILITY_PROFILES[allowedActions] || CAPABILITY_PROFILES.chat;

  const empty = {
    proposals: [],
    rejectedCount: 0,
    rejectedReasons: [],
    parseError: null,
    hasBlock: false,
    contentWithoutBlock: '',
  };

  if (!envelope || typeof envelope !== 'object') {
    return { ...empty, parseError: 'Structured envelope was not an object' };
  }
  const summaryMessage = typeof envelope.summary_message === 'string' ? envelope.summary_message : '';
  const rawProposals = Array.isArray(envelope.proposals) ? envelope.proposals : null;
  if (!rawProposals) {
    return { ...empty, hasBlock: true, contentWithoutBlock: summaryMessage, parseError: 'Envelope missing proposals[]' };
  }

  const proposals = [];
  const rejectedReasons = [];

  rawProposals.forEach((raw, idx) => {
    if (!raw || typeof raw !== 'object' || typeof raw.type !== 'string') {
      rejectedReasons.push(`proposal[${idx}]: not an object or missing type`);
      return;
    }
    const type = raw.type;
    if (!allowed.includes(type)) {
      rejectedReasons.push(`proposal[${idx}] (${type}): not allowed in this capability profile`);
      return;
    }
    const summary = isNonEmptyString(raw.summary) ? raw.summary.trim() : defaultSummary(type, raw);

    // Bridge the structured-envelope's per-type discriminated fields to
    // the legacy `payload`-wrapped shape that applyProposal in App.js
    // already consumes. The schema enforces shape; this is just renaming.
    let payload;
    switch (type) {
      case 'add_workouts':
        if (!raw.add_workouts || !Array.isArray(raw.add_workouts.items)) {
          rejectedReasons.push(`proposal[${idx}] (add_workouts): add_workouts.items missing`);
          return;
        }
        payload = { items: raw.add_workouts.items };
        break;
      case 'remove_workouts':
        if (!raw.remove_workouts || !Array.isArray(raw.remove_workouts.workoutIds)) {
          rejectedReasons.push(`proposal[${idx}] (remove_workouts): remove_workouts.workoutIds missing`);
          return;
        }
        payload = { workoutIds: raw.remove_workouts.workoutIds };
        break;
      case 'add_phase':
        if (!raw.add_phase) {
          rejectedReasons.push(`proposal[${idx}] (add_phase): add_phase payload missing`);
          return;
        }
        payload = { phase: normalizeTargetRaceId(stripNullFields(raw.add_phase)) };
        break;
      case 'modify_phase':
        if (!raw.modify_phase || typeof raw.modify_phase.phaseId !== 'string') {
          rejectedReasons.push(`proposal[${idx}] (modify_phase): modify_phase.phaseId missing`);
          return;
        }
        payload = {
          phaseId: raw.modify_phase.phaseId,
          changes: normalizeTargetRaceId(stripNullFields(raw.modify_phase.changes || {})),
        };
        if (Object.keys(payload.changes).length === 0) {
          rejectedReasons.push(`proposal[${idx}] (modify_phase): no non-null change fields`);
          return;
        }
        break;
      case 'remove_phase':
        if (!raw.remove_phase || typeof raw.remove_phase.phaseId !== 'string') {
          rejectedReasons.push(`proposal[${idx}] (remove_phase): remove_phase.phaseId missing`);
          return;
        }
        payload = { phaseId: raw.remove_phase.phaseId };
        break;
      case 'add_milestone':
        if (!raw.add_milestone) {
          rejectedReasons.push(`proposal[${idx}] (add_milestone): add_milestone payload missing`);
          return;
        }
        payload = { milestone: normalizeTargetRaceId(stripNullFields(raw.add_milestone)) };
        break;
      case 'remove_milestone':
        if (!raw.remove_milestone || typeof raw.remove_milestone.milestoneId !== 'string') {
          rejectedReasons.push(`proposal[${idx}] (remove_milestone): remove_milestone.milestoneId missing`);
          return;
        }
        payload = { milestoneId: raw.remove_milestone.milestoneId };
        break;
      default:
        rejectedReasons.push(`proposal[${idx}] (${type}): unknown type`);
        return;
    }

    proposals.push({ type, summary, payload });
  });

  return {
    proposals,
    rejectedCount: rejectedReasons.length,
    rejectedReasons,
    parseError: null,
    hasBlock: true,
    contentWithoutBlock: summaryMessage,
  };
}

// Strip fields whose value is null. Used to convert "null = not set" from
// the structured schema into "field absent" which the downstream
// applyProposal validators / decorators expect.
function stripNullFields(obj) {
  if (!obj || typeof obj !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== null && v !== undefined) out[k] = v;
  }
  return out;
}

// Defensive normalizer for `targetRaceId`. The schema description says the
// field should be the race's ISO date YYYY-MM-DD, but even with strict
// schema mode the model has been seen to emit composite IDs like
// "Holkham_Half_2026-07-05" or "race:Holkham_Half_2026-07-05" that contain
// a date but aren't usable for race grouping in TrainingPlanPage (which
// matches `targetRaceId` against the race's date string exactly).
//
// This function extracts the first ISO date it finds in the targetRaceId
// string and replaces the value with just that date. If no ISO date is
// present, the field is deleted (the grouping falls through to the
// "Season Plan / unassigned" group, which is at least visible to the
// athlete instead of orphaned into a bogus group of its own).
//
// Mutates the object in place and returns it (call-chain friendly).
function normalizeTargetRaceId(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (typeof obj.targetRaceId !== 'string') return obj;
  const match = obj.targetRaceId.match(/\d{4}-\d{2}-\d{2}/);
  if (match) {
    obj.targetRaceId = match[0];
  } else {
    delete obj.targetRaceId;
  }
  return obj;
}

// ── R2: Tool-call dispatcher ─────────────────────────────────────────────────
// Converts the tool_calls[] array from the OpenAI SDK into the same
// { proposals[], contentWithoutBlock, rejectedCount, rejectedReasons }
// shape that parseStructuredEnvelope produces, so handleStructuredSend in
// App.js needs no structural changes — only a function-name swap.
//
// Reuses stripNullFields, normalizeTargetRaceId, and the CAPABILITY_PROFILES
// gating from above. Does not replace parseStructuredEnvelope (that function
// is retained as a legacy archive reader).
export function dispatchToolCalls(toolCalls, { allowedActions = 'chat', content = '' } = {}) {
  const allowed = Array.isArray(allowedActions)
    ? allowedActions
    : CAPABILITY_PROFILES[allowedActions] || CAPABILITY_PROFILES.chat;

  const proposals = [];
  const clarifications = [];
  const rejectedReasons = [];

  if (!Array.isArray(toolCalls)) {
    return {
      proposals: [],
      clarifications: [],
      rejectedCount: 0,
      rejectedReasons: [],
      parseError: 'tool_calls was not an array',
      hasBlock: false,
      contentWithoutBlock: content,
    };
  }

  toolCalls.forEach((call, idx) => {
    const name = call?.function?.name;
    if (!name) {
      rejectedReasons.push(`tool_call[${idx}]: missing function.name`);
      return;
    }
    if (!allowed.includes(name)) {
      rejectedReasons.push(`tool_call[${idx}] (${name}): not allowed in this capability profile`);
      return;
    }

    let args;
    try {
      args = JSON.parse(call.function.arguments || '{}');
    } catch {
      rejectedReasons.push(`tool_call[${idx}] (${name}): arguments is not valid JSON`);
      return;
    }

    // request_clarification is not a state mutation — it asks the athlete a
    // question instead of acting. Collect it separately; only the FIRST valid
    // clarification is kept (multiple questions in one turn is poor UX).
    if (name === 'request_clarification') {
      if (typeof args.question !== 'string' || !args.question.trim()) {
        rejectedReasons.push(`tool_call[${idx}] (request_clarification): question must be a non-empty string`);
        return;
      }
      if (clarifications.length > 0) {
        rejectedReasons.push(`tool_call[${idx}] (request_clarification): extra clarification ignored (only one per turn)`);
        return;
      }
      const options = Array.isArray(args.options)
        ? args.options
            .filter(o => typeof o === 'string' && o.trim())
            .map(o => o.trim())
            .slice(0, 4)
        : [];
      clarifications.push({ question: args.question.trim(), options });
      return;
    }

    const summary = typeof args.summary === 'string' && args.summary.trim()
      ? args.summary.trim()
      : defaultSummary(name, args);

    let payload;
    switch (name) {
      case 'add_workouts': {
        if (!Array.isArray(args.items) || args.items.length === 0) {
          rejectedReasons.push(`tool_call[${idx}] (add_workouts): items must be a non-empty array`);
          return;
        }
        // Normalise zone: tool schema uses 'none' (strict enums can't have null);
        // applyProposal expects '' or a zone string.
        const items = args.items.map(item => ({
          ...item,
          zone: item.zone === 'none' ? '' : (item.zone || ''),
          description: item.description || '',
        }));
        payload = { items };
        break;
      }
      case 'remove_workouts': {
        if (!Array.isArray(args.workoutIds) || args.workoutIds.length === 0) {
          rejectedReasons.push(`tool_call[${idx}] (remove_workouts): workoutIds must be a non-empty array`);
          return;
        }
        payload = { workoutIds: args.workoutIds };
        break;
      }
      case 'add_phase': {
        if (!args.phase || typeof args.phase !== 'object') {
          rejectedReasons.push(`tool_call[${idx}] (add_phase): phase object missing`);
          return;
        }
        const phase = normalizeTargetRaceId(stripNullFields({
          ...args.phase,
          // Tool schema uses '' for "no race"; normalizer treats missing/empty as unset.
          targetRaceId: args.phase.targetRaceId || null,
        }));
        payload = { phase };
        break;
      }
      case 'modify_phase': {
        if (typeof args.phaseId !== 'string' || !args.phaseId) {
          rejectedReasons.push(`tool_call[${idx}] (modify_phase): phaseId missing`);
          return;
        }
        const changes = normalizeTargetRaceId(stripNullFields(args.changes || {}));
        if (Object.keys(changes).length === 0) {
          rejectedReasons.push(`tool_call[${idx}] (modify_phase): no non-null change fields`);
          return;
        }
        payload = { phaseId: args.phaseId, changes };
        break;
      }
      case 'remove_phase': {
        if (typeof args.phaseId !== 'string' || !args.phaseId) {
          rejectedReasons.push(`tool_call[${idx}] (remove_phase): phaseId missing`);
          return;
        }
        payload = { phaseId: args.phaseId };
        break;
      }
      case 'add_milestone': {
        if (!args.milestone || typeof args.milestone !== 'object') {
          rejectedReasons.push(`tool_call[${idx}] (add_milestone): milestone object missing`);
          return;
        }
        const milestone = normalizeTargetRaceId(stripNullFields({
          ...args.milestone,
          targetRaceId: args.milestone.targetRaceId || null,
          type: args.milestone.type === 'none' ? null : (args.milestone.type || null),
          notes: args.milestone.notes || '',
        }));
        payload = { milestone };
        break;
      }
      case 'remove_milestone': {
        if (typeof args.milestoneId !== 'string' || !args.milestoneId) {
          rejectedReasons.push(`tool_call[${idx}] (remove_milestone): milestoneId missing`);
          return;
        }
        payload = { milestoneId: args.milestoneId };
        break;
      }
      default:
        rejectedReasons.push(`tool_call[${idx}] (${name}): unknown action type`);
        return;
    }

    proposals.push({ type: name, summary, payload });
  });

  // Asking and acting in the same turn is contradictory: if the model emitted
  // a clarification, suppress any coexisting action proposals so the athlete is
  // never asked to confirm a change that was built on a guessed assumption.
  if (clarifications.length > 0 && proposals.length > 0) {
    proposals.splice(0).forEach(p => {
      rejectedReasons.push(`action (${p.type}) suppressed because a clarification was requested in the same turn`);
    });
  }

  return {
    proposals,
    clarifications,
    rejectedCount: rejectedReasons.length,
    rejectedReasons,
    parseError: null,
    hasBlock: toolCalls.length > 0,
    contentWithoutBlock: content,
  };
}

function defaultSummary(type, raw) {
  switch (type) {
    case 'add_workouts':
      return `Add ${Array.isArray(raw.items) ? raw.items.length : Array.isArray(raw.workouts) ? raw.workouts.length : 'some'} workout(s)`;
    case 'remove_workouts':
      return `Remove ${Array.isArray(raw.workoutIds) ? raw.workoutIds.length : 'some'} workout(s)`;
    case 'modify_phase':
      return `Modify a training phase`;
    case 'remove_phase':
      return `Remove a training phase`;
    case 'add_milestone':
      return `Add a milestone`;
    case 'remove_milestone':
      return `Remove a milestone`;
    case 'add_phase':
      return `Add a training phase`;
    case 'set_plan':
      return `Replace the season training plan`;
    default:
      return type;
  }
}
